import { IconBuilding } from "@tabler/icons-react";
import { createFileRoute } from "@tanstack/react-router";
import PortalPage from "src/components/app/PortalPage/PortalPage";
import AssetSearchSettings from "src/components/app/ProjectSettings/AssetSearchSettings";
import DeliverySettings from "src/components/app/ProjectSettings/DeliverySettings";
import ImageMetadataSettings from "src/components/app/ProjectSettings/ImageMetadataSettings";
import ProjectAdminSettings from "src/components/app/ProjectSettings/ProjectAdminSettings";
import ProjectMoveSettings from "src/components/app/ProjectSettings/ProjectMoveSettings";
import ProjectSettings from "src/components/app/ProjectSettings/ProjectSettings";
import TestInputsSettings from "src/components/app/ProjectSettings/TestInputsSettings";
import WebsiteSettings from "src/components/app/ProjectSettings/WebsiteSettings";
import Button from "src/components/common/Button/Button";
import { DeferredLoaderData } from "src/lib/LoaderData";
import { raceLoaderData } from "src/lib/LoaderResult";
import { scopeFrom } from "src/lib/ScopedLoaders";
import { getSettingsPageData } from "src/server/functions/Portal";
import { useExtracted } from "#i18n";

type SettingsPageData = Awaited<ReturnType<typeof getSettingsPageData>>;

export const Route = createFileRoute("/_authed/portal/proj_{$projectKey}/settings")({
  // The page's project settings come from the portal layout; only the search indexing estimate,
  // shown while search is off, is loaded here.
  loader: async (context) => {
    const { project } = await scopeFrom(context);
    if (!project || project.settings?.aiMediaSearchEnabled)
      return raceLoaderData(Promise.resolve<SettingsPageData>({}));
    return raceLoaderData(getSettingsPageData({ data: project.id }));
  },
  component: SettingsPage,
});

function SettingsPage() {
  const translateMessage = useExtracted();
  return (
    <PortalPage>
      <PortalPage.Header>
        <PortalPage.Heading>
          <PortalPage.Title>{translateMessage("Project settings")}</PortalPage.Title>
          <PortalPage.Description>
            {translateMessage("Manage your project’s details and content delivery.")}
          </PortalPage.Description>
        </PortalPage.Heading>
        <PortalPage.Actions>
          <Button
            label={translateMessage("Organisation settings")}
            href="/portal/organisation"
            variant="outline"
          >
            <IconBuilding size={18} aria-hidden />
            {translateMessage("Organisation settings")}
          </Button>
        </PortalPage.Actions>
      </PortalPage.Header>
      <PortalPage.Content>
        <ProjectSettings />
        <DeliverySettings />
        <ImageMetadataSettings />
        <WebsiteSettings />
        <TestInputsSettings />
        <DeferredLoaderData result={Route.useLoaderData()}>
          {(data) => (
            <AssetSearchSettings
              initialProjectId={data?.projectId}
              initialEstimate={data?.initialEstimate}
            />
          )}
        </DeferredLoaderData>
        <ProjectMoveSettings />
        <ProjectAdminSettings />
      </PortalPage.Content>
    </PortalPage>
  );
}
